import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';

// Logo URLs from uploaded assets
const LOGO_HORIZONTAL = "/mezgebpos-logo.png";
const LOGO_COVER = "/mezgebpos-logo.png";

export default function Login() {
    const navigate = useNavigate();
    const { login, register } = useAuth();
    const [loading, setLoading] = useState(false);
    
    const [loginForm, setLoginForm] = useState({ email: '', password: '' });
    const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '', role: 'cashier' });

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(loginForm.email, loginForm.password);
            toast.success('Welcome back!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        const { password } = registerForm;
        if (password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        if (!/[A-Z]/.test(password)) {
            toast.error('Password must contain at least one uppercase letter');
            return;
        }
        if (!/[a-z]/.test(password)) {
            toast.error('Password must contain at least one lowercase letter');
            return;
        }
        if (!/\d/.test(password)) {
            toast.error('Password must contain at least one number');
            return;
        }
        setLoading(true);
        try {
            await register(registerForm.name, registerForm.email, registerForm.password, registerForm.role);
            toast.success('Account created successfully!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-cyan-900/30 via-transparent to-transparent" />
                <div className="relative z-10 flex flex-col justify-center items-center p-12 w-full">
                    <div className="mb-8">
                        <img src={LOGO_COVER} alt="Mezgeb POS" className="w-64 h-auto" />
                    </div>
                    
                    <h1 className="text-3xl text-white mb-2 text-center font-bold">
                        Mezgeb POS
                    </h1>
                    
                    <p className="text-lg text-cyan-400 tracking-wide mb-8">
                        Modern Point of Sale System
                    </p>
                    
                    <p className="text-slate-400 max-w-md text-center leading-relaxed">
                        The complete point-of-sale and inventory management system for restaurants, retail stores, and cafés.
                    </p>
                    
                    <div className="mt-12 grid grid-cols-2 gap-6">
                        {[
                            { value: "99.9%", label: "Uptime" },
                            { value: "24/7", label: "Support" },
                            { value: "500+", label: "Stores" },
                            { value: "1M+", label: "Transactions" }
                        ].map((stat, idx) => (
                            <div key={idx} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                                <div className="text-2xl font-bold text-cyan-400">{stat.value}</div>
                                <div className="text-slate-500 text-sm">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="flex-1 flex items-center justify-center p-8 bg-slate-950">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-8 text-center">
                        <img src={LOGO_HORIZONTAL} alt="Mezgeb POS" className="h-56 w-auto mx-auto mb-4" />
                    </div>

                    <Tabs defaultValue="login" className="w-full">
                        <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-900">
                            <TabsTrigger 
                                value="login" 
                                data-testid="login-tab"
                                className="data-[state=active]:bg-cyan-500 data-[state=active]:text-slate-950"
                            >
                                Login
                            </TabsTrigger>
                            <TabsTrigger 
                                value="register" 
                                data-testid="register-tab"
                                className="data-[state=active]:bg-cyan-500 data-[state=active]:text-slate-950"
                            >
                                Register
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="login">
                            <Card className="bg-slate-900 border-slate-800">
                                <CardHeader>
                                    <CardTitle className="text-white">Welcome back</CardTitle>
                                    <CardDescription className="text-slate-400">Enter your credentials to access your account</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleLogin} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="email" className="text-slate-300">Email</Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                placeholder="name@example.com"
                                                value={loginForm.email}
                                                onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                                                required
                                                data-testid="login-email"
                                                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="password" className="text-slate-300">Password</Label>
                                            <Input
                                                id="password"
                                                type="password"
                                                placeholder="••••••••"
                                                value={loginForm.password}
                                                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                                                required
                                                data-testid="login-password"
                                                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                                            />
                                        </div>
                                        <Button 
                                            type="submit" 
                                            className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold" 
                                            disabled={loading}
                                            data-testid="login-submit"
                                        >
                                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        <TabsContent value="register">
                            <Card className="bg-slate-900 border-slate-800">
                                <CardHeader>
                                    <CardTitle className="text-white">Create an account</CardTitle>
                                    <CardDescription className="text-slate-400">Get started with Mezgeb POS today</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleRegister} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="name" className="text-slate-300">Full Name</Label>
                                            <Input
                                                id="name"
                                                placeholder="John Doe"
                                                value={registerForm.name}
                                                onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                                                required
                                                data-testid="register-name"
                                                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="reg-email" className="text-slate-300">Email</Label>
                                            <Input
                                                id="reg-email"
                                                type="email"
                                                placeholder="name@example.com"
                                                value={registerForm.email}
                                                onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                                                required
                                                data-testid="register-email"
                                                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="reg-password" className="text-slate-300">Password</Label>
                                            <Input
                                                id="reg-password"
                                                type="password"
                                                placeholder="••••••••"
                                                value={registerForm.password}
                                                onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                                                required
                                                data-testid="register-password"
                                                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                                            />
                                        </div>
                                        <Button 
                                            type="submit" 
                                            className="w-full bg-cyan-500 hover:bg-cyan-600 text-slate-950 font-semibold" 
                                            disabled={loading}
                                            data-testid="register-submit"
                                        >
                                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Account'}
                                        </Button>
                                    </form>
                                    
                                    <div className="mt-4 p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                                        <div className="flex items-center gap-2 text-cyan-400 font-medium text-sm mb-1">
                                            <Sparkles className="h-4 w-4" />
                                            14-Day Free Trial
                                        </div>
                                        <p className="text-xs text-slate-400">
                                            Create your account, then start a free trial from the pricing page. No credit card required.
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                    
                    <p className="text-center text-sm text-slate-500 mt-6">
                        <a href="/" className="text-cyan-400 hover:underline">← Back to home</a>
                    </p>
                </div>
            </div>
        </div>
    );
}
